import { useState } from 'react'
import { Link } from 'react-router-dom'
import PageHero from '@/components/PageHero'
import { Reveal, ImageFrame } from '@/components/Reveal'
import { HelixBand } from '@/components/Helix'
import { makeHybridSpecs, hybrids, type Hybrid, type HybridSpecRow } from '@/data/site-content'

const defaultSpecRows: Array<[string, string | null]> = [
  ['GENERAL', null],
  ['Hybrid Code', 'code'],
  ['Grain Type', 'type'],
  ['Maturity Group', 'maturity'],
  ['Breeding Method', 'Conventional (NON-GMO)'],
  ['Heterotic Group', '[TO BE FILLED]'],
  ['AGRONOMIC', null],
  ['Plant / Ear Height', '[TO BE FILLED]'],
  ['Seedling Vigour (1–9)', '[TO BE FILLED]'],
  ['Root Strength (1–9)', '[TO BE FILLED]'],
  ['Standability (1–9)', '[TO BE FILLED]'],
  ['Stay-Green (1–9)', '[TO BE FILLED]'],
  ['ADAPTATION', null],
  ['Recommended Regions', '[TO BE FILLED]'],
  ['Yield Potential', '[TO BE FILLED]'],
  ['Dryland / Irrigation', '[TO BE FILLED]'],
  ['Optimal Plant Population', '[TO BE FILLED]'],
  ['DISEASE TOLERANCE', null],
  ['Grey Leaf Spot / NCLB / Rust', '[TO BE FILLED]'],
  ['Stalk Rot / Ear Rot', '[TO BE FILLED]'],
  ['GRAIN QUALITY', null],
  ['Grain Type / Test Weight', '[TO BE FILLED]'],
  ['Parental Lines (F × M)', '[TO BE FILLED]'],
]

function isHeading(row: HybridSpecRow): row is { heading: string } {
  return 'heading' in row
}

function HybridCard({ h }: { h: Hybrid }) {
  const [open, setOpen] = useState(false)
  const specRows = h.specs ?? makeHybridSpecs(h, defaultSpecRows)

  return (
    <div className="hx-card flex flex-col">
      <div className="overflow-hidden" style={{ aspectRatio: '16/9' }}>
        <img
          src={h.image}
          alt={`${h.code} — ${h.type}`}
          className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
          loading="lazy"
        />
      </div>
      <div className="flex flex-1 flex-col p-8 md:p-9">
        <div className="flex items-baseline justify-between">
          <span className="font-mono-data text-2xl font-light tracking-wide" style={{ color: '#24391C' }}>
            {h.code}
          </span>
          <span className="font-mono-data text-[11px] tracking-[0.06em]" style={{ color: '#C98E33' }}>
            {h.type}
          </span>
        </div>
        <div className="mt-4 flex gap-5 font-mono-data text-[12px]" style={{ color: '#96968A' }}>
          <span>{h.days}</span>
          <span>·</span>
          <span>{h.maturity}</span>
        </div>
        <p className="mt-5 text-[14px] leading-relaxed" style={{ color: '#565650' }}>
          {h.note}
        </p>
        <ul className="mt-5 space-y-2.5">
          {h.strengths.map((s) => (
            <li key={s} className="flex items-baseline gap-3 text-[14px]" style={{ color: '#565650' }}>
              <span className="inline-block h-[5px] w-[5px] rotate-45" style={{ backgroundColor: '#C98E33' }} />
              {s}
            </li>
          ))}
        </ul>
        <div className="mt-auto flex flex-wrap items-center gap-5 pt-8">
          <button onClick={() => setOpen(!open)} className="link-underline">
            {open ? 'Hide spec sheet' : 'View spec sheet'}
          </button>
          {h.datasheet && (
            <a
              href={h.datasheet}
              target="_blank"
              rel="noopener noreferrer"
              className="link-underline"
              style={{ color: '#C98E33' }}
            >
              Datasheet (PDF) →
            </a>
          )}
          <Link to="/#contact" className="link-underline" style={{ color: '#C98E33' }}>
            Enquire →
          </Link>
        </div>
      </div>

      {/* expandable spec sheet */}
      <div
        className="grid transition-[grid-template-rows] duration-500 ease-out"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="border-t p-8 md:p-9" style={{ borderColor: '#D9DED5', backgroundColor: '#FAFBF9' }}>
            <div className="font-mono-data text-[11px] uppercase tracking-[0.2em]" style={{ color: '#96968A' }}>
              Specification — {h.code}
            </div>
            <dl className="mt-5 space-y-0 font-mono-data text-[12px]">
              {specRows.map((row, i) =>
                isHeading(row) ? (
                  <div key={i} className="pt-4 text-[10px] uppercase tracking-[0.2em]" style={{ color: '#C98E33' }}>
                    {row.heading}
                  </div>
                ) : (
                  <div key={i} className="flex justify-between gap-6 border-b py-2.5" style={{ borderColor: '#E6EAE3' }}>
                    <dt style={{ color: '#96968A' }}>{row.label}</dt>
                    <dd className="text-right" style={{ color: row.value === '[TO BE FILLED]' ? '#C2C8BE' : '#24391C' }}>
                      {row.value}
                    </dd>
                  </div>
                )
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Hybrids() {
  const { hero, philosophy, portfolio, quality } = hybrids

  return (
    <>
      <PageHero eyebrow={hero.eyebrow} title={hero.title}>
        {hero.body}
      </PageHero>

      <section className="py-24 md:py-32" style={{ backgroundColor: '#F3F5F1' }}>
        <div className="container-hx max-w-3xl">
          <Reveal className="eyebrow">{philosophy.eyebrow}</Reveal>
          <Reveal as="p" delay={0.1} className="mt-6 text-[clamp(1.15rem,2vw,1.45rem)] font-light leading-[1.7]" style={{ color: '#24391C' }}>
            {philosophy.body}
          </Reveal>
        </div>
        <div className="container-hx mt-16">
          <HelixBand height={40} turns={10} />
        </div>
      </section>

      <section className="pb-24 md:pb-32" style={{ backgroundColor: '#F3F5F1' }}>
        <div className="container-hx">
          <Reveal className="eyebrow">{portfolio.eyebrow}</Reveal>
          <Reveal as="h2" delay={0.08} className="mt-5 mb-14 text-[clamp(1.8rem,3.2vw,2.5rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]">
            {portfolio.title}
          </Reveal>
          <div className="grid gap-8 lg:grid-cols-2">
            {portfolio.hybrids.map((h) => (
              <HybridCard key={h.code} h={h} />
            ))}
          </div>
          <Reveal className="mt-16 text-center">
            <p className="text-sm" style={{ color: '#96968A' }}>
              {portfolio.footerNote}
            </p>
            <Link to={portfolio.cta.path} className="btn-primary mt-7 inline-flex">
              {portfolio.cta.label}
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28" style={{ backgroundColor: '#E9EDE7' }}>
        <div className="container-hx grid items-center gap-12 lg:grid-cols-2">
          <ImageFrame src={quality.image} alt={quality.imageCaption} ratio="3/2" caption={quality.imageCaption} />
          <div>
            <Reveal className="eyebrow">{quality.eyebrow}</Reveal>
            <Reveal as="h2" delay={0.08} className="mt-5 text-[clamp(1.6rem,2.8vw,2.2rem)] font-light leading-[1.25] tracking-[-0.01em]">
              {quality.title}
            </Reveal>
            <Reveal as="p" delay={0.16} className="mt-6 leading-[1.85]" style={{ color: '#565650' }}>
              {quality.body}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  )
}
